import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { MembersSelect } from "@/components/molecules/members-select";
import { ServiceMultiSelect } from "@/components/molecules/service-select";
import { useTranslation } from "@/features/utils/i18n";
import { FunnelIcon } from "@phosphor-icons/react";
import { useState } from "react";

interface CalendarFiltersProps {
  selectedMembers: string[];
  onMembersChange: (members: string[]) => void;
  selectedServices: string[];
  onServicesChange: (services: string[]) => void;
  isMobile?: boolean;
}

export function CalendarFilters({
  selectedMembers,
  onMembersChange,
  selectedServices,
  onServicesChange,
  isMobile = false,
}: CalendarFiltersProps) {
  const { t } = useTranslation("calendar");
  const [open, setOpen] = useState(false);

  // Check if any filters are active
  const hasActiveFilters =
    selectedMembers.length > 0 || selectedServices.length > 0;

  // Desktop version - inline filters
  if (!isMobile) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-56">
          <MembersSelect
            value={selectedMembers}
            onChange={onMembersChange}
            multiple
            placeholder={t("filters.filterByMembers")}
            showBadges={false}
          />
        </div>
        <div className="w-56">
          <ServiceMultiSelect
            value={selectedServices}
            onChange={onServicesChange}
            placeholder={t("filters.filterByServices")}
            showBadges={false}
          />
        </div>
      </div>
    );
  }

  // Mobile version - dialog with button
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={hasActiveFilters ? "default" : "outline"} size="sm">
          <FunnelIcon weight="fill" className="size-4" />
          {t("filters.label")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("filters.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t("filters.members")}
            </label>
            <MembersSelect
              value={selectedMembers}
              onChange={onMembersChange}
              multiple
              placeholder={t("filters.filterByMembers")}
              showBadges={true}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t("filters.services")}
            </label>
            <ServiceMultiSelect
              value={selectedServices}
              onChange={onServicesChange}
              placeholder={t("filters.filterByServices")}
              showBadges={true}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
